import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Ruler, BedDouble, Eye, Users, Check } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { CONFIG, UNITS } from "@/staticData";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import BookingSection from "@/components/BookingSection";
import Lightbox from "@/components/Lightbox";
import { GALLERIES } from "@/roomGalleries";


const DEFAULT_PREFILL = { unitType: "room", checkIn: "", checkOut: "", adults: 2, children: 0, pet: false };

export default function RoomPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [units] = useState(UNITS.filter((u) => u.active));
  const [config] = useState(CONFIG);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const toTop = () => {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, [slug]);

  const unit = units.find((u) => u.slug === slug);

  useEffect(() => {
    if (units.length && !unit) navigate("/");
  }, [units, unit]);

  const close = useCallback(() => setOpen(null), []);
  const gallery = GALLERIES[slug] || [];
  const prev = useCallback(() => setOpen((o) => (o === null ? null : (o - 1 + gallery.length) % gallery.length)), [gallery.length]);
  const next = useCallback(() => setOpen((o) => (o === null ? null : (o + 1) % gallery.length)), [gallery.length]);

  if (!unit) return <div className="min-h-screen bg-cream" />;

  const name = unit[`name_${lang}`] || unit.name_en || unit.name_ro;
  const desc = unit[`desc_${lang}`] || unit.desc_en || unit.desc_ro;
  const view = unit[`view_${lang}`] || unit.view_en || unit.view_ro;
  const roomAmenities = t("am.room");

  return (
    <div className="relative" data-testid="room-page">
      <Header config={config} />
      <main className="pt-28 md:pt-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brown/60 hover:text-terracotta transition-colors" data-testid="room-back">
            <ArrowLeft size={16} /> {t("rp.back")}
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
                <span className="inline-block w-10 h-px bg-terracotta" />
                {unit.type === "house" ? t("rp.type_house") : t("rp.type_room")}
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-4 font-serif font-medium text-5xl md:text-6xl leading-[1.03] tracking-tight text-main" data-testid="room-title">
                {name}
              </motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.25 }}
                className="mt-5 text-base md:text-lg text-brown font-light leading-relaxed max-w-xl" data-testid="room-desc">
                {desc}
              </motion.p>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-4 text-sm text-brown/70 font-light" data-testid="room-specs">
                {unit[`tag_${lang}`] ? `${unit[`tag_${lang}`]} · ` : ""}{unit[`floor_${lang}`] || unit.floor_en || unit.floor_ro} · {t("rpx.shower")} {unit.shower}
              </motion.p>
              {(unit[`saying_${lang}`] || (lang !== 'ro' && unit.saying_en)) && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.35 }}
                  className="mt-5 font-serif italic text-lg text-green-dark max-w-xl" data-testid="room-saying">
                  {t("rpx.saying")}: „{unit[`saying_${lang}`] || unit.saying_en}"
                </motion.p>
              )}
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase font-bold text-walnut/70"
                data-testid="room-vt-badge">
                <img src="/assets/vt-marker.png" alt="Via Transilvanica" className="w-5 h-5" />
                {t("vt.badge")}
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }}
                className="mt-8 flex flex-wrap gap-3" data-testid="room-meta">
                {[[Ruler, t("rp.size"), unit.size], [BedDouble, t("rp.bed"), unit.bed], [Eye, t("rp.view"), view],
                  [Users, t("rp.capacity"), `${unit.capacity_adults} ${t("rp.adults")} + ${unit.capacity_children} ${t("rp.children")}`]].map(([Icon, label, val]) => (
                  <div key={label} className="flex items-center gap-2.5 bg-cream-light border border-warm-grey/50 rounded-full px-4 py-2.5">
                    <Icon size={16} className="text-terracotta" />
                    <span className="text-xs text-walnut font-bold uppercase tracking-wider">{label}</span>
                    <span className="text-sm text-main font-semibold">{val}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}
                className="mt-9 flex flex-wrap items-center gap-6">
                <p className="font-serif text-4xl text-terracotta" data-testid="room-price">
                  <span className="text-sm text-brown/60 font-sans align-top">{t("rp.from")} </span>
                  {unit.base_price}€<span className="text-lg text-brown/60">{t("rp.night")}</span>
                </p>
                <button onClick={() => scrollToId("#rezervare-camera")} data-testid="room-book-cta"
                  className="rounded-full bg-terracotta text-cream-light font-semibold px-7 py-3.5 hover:bg-terracotta/90 hover:scale-[1.03] transition-all duration-300 shadow-lg shadow-terracotta/20">
                  {t("rp.book")} · {name}
                </button>
              </motion.div>
            </div>

            <div className="lg:col-span-6">
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }}
                className="overflow-hidden rounded-t-[160px] rounded-b-3xl aspect-[4/3] cursor-pointer group" onClick={() => setOpen(0)}>
                <img src={gallery[0]?.src || unit.image} alt={name} data-testid="room-hero-image"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </motion.div>
            </div>
          </div>

          {gallery.length > 1 && (
            <div className="mt-20">
              <h2 className="font-serif text-3xl md:text-4xl text-main" data-testid="room-gallery-title">{t("rp.gallery")}</h2>
              <div className="mt-8 columns-2 md:columns-3 gap-4" data-testid="room-gallery-grid">
                {gallery.map((img, i) => (
                  <button key={img.src + i} onClick={() => setOpen(i)} data-testid={`room-gallery-item-${i}`}
                    className="group relative block w-full mb-4 break-inside-avoid overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-terracotta/60">
                    <img src={img.src} alt={img.alt[lang] || img.alt.en || img.alt.ro} loading="lazy"
                      className="w-full h-auto group-hover:scale-105 transition-transform duration-700" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-20 bg-cream-light border border-warm-grey/40 rounded-3xl p-8 md:p-12" data-testid="room-amenities">
            <h2 className="font-serif text-3xl md:text-4xl text-main">{t("rp.amenities")}</h2>
            <ul className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3.5">
              {roomAmenities.concat(unit[`extras_${lang}`] || unit.extras_en || []).map((a) => (
                <li key={a} className="flex items-center gap-3 text-sm text-brown/90 font-light">
                  <Check size={16} className="text-green-dark shrink-0" /> {a}
                </li>
              ))}
            </ul>
            {unit.type === "room" && (
              <p className="mt-7 text-sm text-green-dark font-medium">{t("rp.shared")}</p>
            )}
            {unit.type === "house" && (
              <div className="mt-7" data-testid="house-includes-rooms">
                <p className="text-sm text-green-dark font-medium">{t("rp.includes_rooms")}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {units.filter((u) => u.type === "room").map((r) => (
                    <Link key={r.id} to={`/cazare/${r.slug}`} data-testid={`house-room-link-${r.slug}`}
                      className="rounded-full border border-warm-grey/60 px-4 py-2 text-xs font-semibold text-brown hover:border-terracotta hover:text-terracotta transition-all duration-300">
                      {r[`name_${lang}`] || r.name_ro}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div id="rezervare-camera" className="mt-20">
          <BookingSection units={units} config={config} prefill={DEFAULT_PREFILL} fixedUnit={unit.id} />
        </div>
      </main>
      <Footer config={config} />
      <WhatsAppFloat config={config} />
      <Lightbox images={gallery} index={open} onClose={close} onPrev={prev} onNext={next} lang={lang} />
    </div>
  );
}
